import React, { useMemo } from 'react'
import { FileWarning, LogOut, MessageCircle, Phone } from 'lucide-react'
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  clearManagementCache,
  readCachedPortal,
  telSupportHref,
} from '../../services/managementService'

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).slice(0, 10))
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Hard lock when all contracts are expired. SA bypasses in Home/Login.
 */
export default function ContractExpired() {
  const portal = useMemo(() => readCachedPortal(), [])
  const expiredContracts = (portal?.Contracts || []).filter((c) => c.IsExpired)
  const latest = expiredContracts[0] || portal?.Contracts?.[0]

  const companyName =
    portal?.CustomerName ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem('companyProfile') || '{}').Name || 'Your company'
      } catch {
        return 'Your company'
      }
    })()

  const waUrl = buildWhatsAppUrl({
    customerName: companyName,
    registerCode: portal?.Code,
    reason: 'Our software contract has expired and we need to renew to continue using the product.',
    details: latest
      ? `Contract: ${latest.ContractNo || '—'}; Ended: ${formatDate(latest.EndDate)}`
      : undefined,
  })

  const handleLogout = () => {
    clearManagementCache()
    localStorage.removeItem('authToken')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('companyProfile')
    window.location.href = '/login'
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top, #ffedd5 0%, #f8fafc 45%, #e2e8f0 100%)',
        }}
      />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-400/20 blur-3xl" />
      <div className="absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-amber-300/25 blur-3xl" />

      <div className="relative w-full max-w-lg">
        <div className="rounded-3xl bg-white/90 backdrop-blur shadow-2xl border border-white/80 overflow-hidden">
          <div className="px-8 pt-10 pb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
              <FileWarning className="h-8 w-8" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-orange-600">
              Access restricted
            </p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900 tracking-tight">
              Contract expired
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-sm mx-auto">
              Your software contract has ended. Renew the contract to continue using this product.
            </p>
          </div>

          <div className="px-8 pb-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Account
                  </p>
                  <p className="mt-1 font-semibold text-slate-900 truncate">{companyName}</p>
                  {portal?.Code ? (
                    <p className="text-xs text-slate-500 mt-0.5">Register code · {portal.Code}</p>
                  ) : null}
                </div>
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
                  Expired
                </span>
              </div>
              {latest ? (
                <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-600">
                  <p>
                    <span className="font-medium text-slate-800">
                      {latest.ContractNo || 'Contract'}
                    </span>
                    {latest.ProductName ? ` · ${latest.ProductName}` : ''}
                  </p>
                  <p className="mt-0.5">Ended on {formatDate(latest.EndDate)}</p>
                </div>
              ) : null}
            </div>
          </div>

          <div className="px-8 pb-6">
            <p className="text-center text-xs text-slate-500">
              Contact Ainsoft support to renew your contract and restore access.
            </p>
          </div>

          <div className="px-8 pb-8 space-y-2.5">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:brightness-95 transition"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp support
            </a>
            <a
              href={telSupportHref()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition"
            >
              <Phone className="h-4 w-4" />
              Call {SUPPORT_MOBILE}
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          Ainsoft Delivery · contract renewal required
        </p>
      </div>
    </div>
  )
}
